import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { FolderFormDataCreateInput, FolderFormDataObjectDepre, FolderFormDataUpdateInput } from '@it2go/types';
import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { FormBaseFormStore } from './form-base-form.store';
import { ActivatedRoute } from '@angular/router';
import { FieldErrorFactory } from 'packages/client/src/app/modules/shared/service/field-error.service';
import { FormsTotalPoints } from '@libs/shared/form-helpers/forms/forms-total-points';
import { FormsFinalEvaluation } from '@libs/shared/form-helpers/forms/forms-final-evaluation';
import { isAllowed } from '../../../../../shared/shared.global-service';
import { AppFormGroup } from '../../../../../../utils/form.utils';

export interface FormItem {
    name: string,
    defaultValue: any,
    required: boolean,
    patchValueCallback?: (value: any) => any,
}

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [FormBaseFormStore],
})
export abstract class FormBaseFormComponent extends SubscriptionComponent implements OnInit {
    public readonly route = inject(ActivatedRoute);
    public readonly store = inject(FormBaseFormStore);

    @Input() folderFormId: number | null = null;
    @Input() folderFormData: FolderFormDataObjectDepre | null = null;
    @Input() copy = false;
    @Output() submitForm = new EventEmitter<FolderFormDataCreateInput | FolderFormDataUpdateInput>();
    @Output() closeForm = new EventEmitter<void>();

    options: { value: any, name: string }[] = [];

    items: FormItem[] = [];

    form = new AppFormGroup({});
    formGenerated = false;

    folderId: string | null = null;
    formUniqueIdentifier: string | null = null;
    keys: string[] = [];

    totalPoints: number = 0;
    finalEvaluation: string = '';

    protected constructor() {
        super();
        this.subs.push(
            this.form.valueChanges.subscribe((newValues) => {
                this.generateFinalEvaluation(newValues);
            }),
        );
    }

    ngOnInit() {
        this.folderId = this.route.snapshot.paramMap.get('folderId');
        const currentUserId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
        if (this.folderFormData) {
            const rawData: unknown = this.folderFormData.data;
            const parsedData: unknown = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
            const jsonData: Record<string, unknown> = parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)
                ? parsedData as Record<string, unknown>
                : {};
            this.items.forEach((item) => {
                if (item.patchValueCallback) {
                    this.form.patchValue({
                        [item.name]: item.patchValueCallback(jsonData[item.name]),
                    }, { emitEvent: false });
                } else {
                    this.form.patchValue({
                        [item.name]: jsonData[item.name],
                    }, { emitEvent: false });
                }
            });
            this.form.patchValue({
                user: this.copy ? currentUserId || this.folderFormData.userId : this.folderFormData.userId,
                date: this.copy ? new Date() : new Date(this.folderFormData.date),
            });
        } else {
            this.form.patchValue({
                user: currentUserId,
            });
        }

    }

    submit() {
        if (isInvalid(this.form)) return;

        const formData: { [k: string]: any } = {};
        this.items.forEach((item) => {
            formData[item.name] = (this.form.controls as any)[item.name].value;
        });
        const data = JSON.stringify(formData);
        const userValue = (this.form.controls as any).user.value;
        const date = (this.form.controls as any).date.value as Date;
        date.setHours(12, 0, 0, 0);

        const timeValue = (this.form.controls as any).time?.value as Date;
        if (timeValue) {
            date.setHours(timeValue.getHours(), timeValue.getMinutes(), 0, 0);
        }

        const dateValue = date.toISOString();

        if (this.folderFormData && !this.copy) {
            this.submitForm.emit({
                id: this.folderFormData.id,
                userId: userValue,
                date: dateValue,
                data,
            } as FolderFormDataUpdateInput);
        } else if (this.folderFormId) {
            this.submitForm.emit({
                folderFormId: this.folderFormId,
                userId: userValue,
                date: dateValue,
                data,
            } as FolderFormDataCreateInput);
        }
    }

    protected generateFinalEvaluation(newValues: any) {
        if (this.formUniqueIdentifier && this.keys.length && this.formGenerated) {
            const formData: { [k: string]: any } = {};
            this.keys.forEach((key) => {
                formData[key] = newValues[key] ?? (this.form.controls as any)[key].value;
            });
            const formDataString = JSON.stringify(formData);
            this.totalPoints = FormsTotalPoints.get(formDataString, this.formUniqueIdentifier);
            this.finalEvaluation = FormsFinalEvaluation.get(formDataString, this.formUniqueIdentifier);
        }
    }

    protected generateForm(items: FormItem[], oncePerDayValidation = false) {
        this.items = items;
        items.forEach((item) => {
            this.form.addControl(item.name, new FormControl<number>(
                item.defaultValue,
                item.required ? Validators.required : [],
            ));
        });
        this.form.addControl('user', new FormControl(null, Validators.required));

        const defaultDate = new Date(new Date().setHours(12, 0, 0, 0));
        if (oncePerDayValidation) {
            this.form.addControl('date', new FormControl(defaultDate, Validators.required, this.oncePerDayValidator()));
            (this.form.controls as any)['date'].markAsTouched();
        } else {
            this.form.addControl('date', new FormControl(defaultDate, Validators.required));
        }
        this.formGenerated = true;
        if (!isAllowed['form.update']()) {
            this.form.disable();
        }
    }

    private oncePerDayValidator(): AsyncValidatorFn {
        return async (control: AbstractControl): Promise<ValidationErrors | null> => {
            if (control.value && this.folderId && this.formUniqueIdentifier) {
                this.store.api.checkFolderFormDataExistingAtDate({
                    folderId: Number(this.folderId),
                    uniqueIdentifier: this.formUniqueIdentifier,
                    date: control.value.toISOString(),
                }).then((it) => {
                    // const folderFormDataFromSameDate = await this.st.get(
                    //     this.st.apiSuccess.checkFolderFormDataExistingAtDate,
                    // ) as unknown as any;
                    const isCreate = this.copy || !this.folderFormData;
                    if (it.length > 1 ||
                        (it.length === 1 && (isCreate || (this.folderFormData && it[0].id !== this.folderFormData.id)))) {
                        return FieldErrorFactory.folderFormDataAlreadyExistsError();
                    }

                    return null;
                });
            }

            return null;
        };
    }
}
